// Sprint 9.3 — Impostazioni studio (profilo + branding + role)
//
// 5 sezioni gestite con form locali + mutation react-query verso /api/pro/profile.
// Refresh /api/auth/me dopo update (nome studio + pro_kind compaiono in shell topbar).

import { useState, useEffect, useMemo } from 'react';
import { Save, Copy, Check, AlertTriangle, ExternalLink, LogOut, Loader2, Calendar, Clock, RefreshCw, Link2Off, Link2, CreditCard } from 'lucide-react';
import { ProTopBar } from '../components/ProTopBar';
import { LogoUploader } from '../components/LogoUploader';
import { ColorPicker } from '../components/ColorPicker';
import { useAuth, API_BASE } from '../lib/auth';
import {
  useStudioProfile,
  useUpdateStudioProfile,
  useUploadStudioLogo,
  useDeleteStudioLogo,
  useCalcomStatus,
  useDisconnectCalcom,
  useSyncCalcom,
  useMySubscription,
} from '../lib/queries';
import type { StudioProKind, StudioProfile, UpdateProfileBody, CalcomSyncResult } from '../lib/api';
import { ApiError, studioLogoSrc, calcomConnectUrl, getStoredSessionId, buyAiPack, buyModuleAddon } from '../lib/api';
import styles from './Impostazioni.module.css';

const DEFAULT_PRIMARY = '#0b8a3e';
const DEFAULT_ACCENT = '#ff7a1a';

// La SPA paziente (dietamenu.schedy.app) gestisce ?invite=<pro_code> al bootstrap
// → auth-register con codice precompilato. NON esiste un path /onboarding sulla SPA
// (era una route del pro-web React → 404 per il paziente). Fix 2026-05-29.
const INVITE_LINK_BASE = 'https://dietamenu.schedy.app/';

// ---------------------------------------------------------------------------
// Toast hook (local)
// ---------------------------------------------------------------------------

type ToastKind = 'success' | 'error' | 'info';
interface ToastState {
  kind: ToastKind;
  msg: string;
}

function useToast() {
  const [toast, setToast] = useState<ToastState | null>(null);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);
  return { toast, setToast };
}

function Toast({ toast }: { toast: ToastState | null }) {
  if (!toast) return null;
  const cls =
    toast.kind === 'error'
      ? `${styles.toast} ${styles.toastError}`
      : toast.kind === 'success'
        ? `${styles.toast} ${styles.toastSuccess}`
        : styles.toast;
  return (
    <div className={cls} role="status" aria-live="polite">
      {toast.kind === 'error' && <AlertTriangle size={16} />}
      {toast.kind === 'success' && <Check size={16} />}
      <span>{toast.msg}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------

export default function Impostazioni() {
  const { refresh: refreshAuth } = useAuth();
  const profileQ = useStudioProfile();
  const profile = profileQ.data;

  return (
    <>
      <ProTopBar
        breadcrumb="Studio Pro · Configurazione"
        title="Impostazioni"
      />
      {profileQ.isLoading && (
        <div className={styles.skeleton}>Caricamento impostazioni…</div>
      )}
      {profileQ.isError && (
        <div className={styles.errorBox}>
          Errore caricamento profilo: {profileQ.error?.message || 'sconosciuto'}
        </div>
      )}
      {profile && <ImpostazioniContent profile={profile} refreshAuth={refreshAuth} />}
    </>
  );
}

interface ContentProps {
  profile: StudioProfile;
  refreshAuth: () => Promise<void>;
}

function ImpostazioniContent({ profile, refreshAuth }: ContentProps) {
  const { toast, setToast } = useToast();

  // Sprint 8.4 — read URL ?calcom=ok|err per mostrare toast post-callback OAuth.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const calStatus = params.get('calcom');
    if (calStatus === 'ok') {
      setToast({ kind: 'success', msg: 'Cal.com collegato con successo' });
      params.delete('calcom');
      params.delete('msg');
      const newQs = params.toString();
      const newUrl = window.location.pathname + (newQs ? '?' + newQs : '') + window.location.hash;
      window.history.replaceState({}, '', newUrl);
    } else if (calStatus === 'err') {
      const msg = params.get('msg') || 'Errore collegamento Cal.com';
      setToast({ kind: 'error', msg: `Cal.com: ${decodeURIComponent(msg)}` });
      params.delete('calcom');
      params.delete('msg');
      const newQs = params.toString();
      const newUrl = window.location.pathname + (newQs ? '?' + newQs : '') + window.location.hash;
      window.history.replaceState({}, '', newUrl);
    }
    // Eseguito una sola volta al mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2026-06-11 — read URL ?checkout=success|canceled al rientro da Stripe
  // (success_url/cancel_url role-aware puntano qui). Il param va rimosso
  // subito o un refresh lo rigiocherebbe (postmortem ?reset=1).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get('checkout');
    if (!checkout) return;
    if (checkout === 'success') {
      // L'attivazione arriva dal webhook Stripe: può richiedere qualche secondo.
      setToast({ kind: 'success', msg: 'Pagamento completato! L\'abbonamento si attiva a breve.' });
    } else if (checkout === 'canceled') {
      setToast({ kind: 'error', msg: 'Checkout annullato. Puoi riprovare quando vuoi.' });
    }
    params.delete('checkout');
    const newQs = params.toString();
    const newUrl = window.location.pathname + (newQs ? '?' + newQs : '') + window.location.hash;
    window.history.replaceState({}, '', newUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.page}>
      <Toast toast={toast} />
      <ProfileSection profile={profile} setToast={setToast} refreshAuth={refreshAuth} />
      <BrandingSection profile={profile} setToast={setToast} refreshAuth={refreshAuth} />
      <RoleSection profile={profile} setToast={setToast} refreshAuth={refreshAuth} />
      <InviteCodeSection profile={profile} setToast={setToast} />
      <CalcomSection setToast={setToast} />
      <AccountSection profile={profile} setToast={setToast} />
      <UsageSection />
    </div>
  );
}

// ---------------------------------------------------------------------------
// 1) Profilo professionale
// ---------------------------------------------------------------------------

interface SectionProps {
  profile: StudioProfile;
  setToast: (t: ToastState | null) => void;
  refreshAuth?: () => Promise<void>;
}

function ProfileSection({ profile, setToast, refreshAuth }: SectionProps) {
  const [name, setName] = useState(profile.name);
  const [note, setNote] = useState(profile.pro_note_text || '');
  const updateMut = useUpdateStudioProfile();

  // Reset locale quando arrivano nuovi dati dal server
  useEffect(() => {
    setName(profile.name);
    setNote(profile.pro_note_text || '');
  }, [profile.name, profile.pro_note_text]);

  const dirty =
    name.trim() !== profile.name ||
    (note || '') !== (profile.pro_note_text || '');

  const onSave = () => {
    if (!dirty) return;
    const body: UpdateProfileBody = {};
    if (name.trim() !== profile.name) body.name = name.trim();
    if ((note || '') !== (profile.pro_note_text || '')) {
      body.pro_note_text = note.length > 0 ? note : null;
    }
    updateMut.mutate(body, {
      onSuccess: () => {
        setToast({ kind: 'success', msg: 'Profilo aggiornato' });
        // IIFE async perché onSuccess è sincrono ma refreshAuth è async
        void refreshAuth?.();
      },
      onError: (err) => {
        const msg = err instanceof ApiError ? err.message : (err.message || 'Errore');
        setToast({ kind: 'error', msg });
      },
    });
  };

  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <h2 className={styles.cardTitle}>Profilo professionale</h2>
        <p className={styles.cardSub}>
          Nome, email e nota pinnata visibile ai tuoi pazienti.
        </p>
      </header>
      <div className={styles.cardBody}>
        <div className={styles.row}>
          <div className={styles.rowLabel}>
            <div className={styles.rowLabelMain}>Nome e cognome</div>
            <div className={styles.rowLabelHint}>Visualizzato come firma nelle comunicazioni.</div>
          </div>
          <div className={styles.rowControl}>
            <input
              type="text"
              className={styles.input}
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              placeholder="Dott. Mario Rossi"
              disabled={updateMut.isPending}
              aria-label="Nome"
            />
            <div className={styles.inputCount}>{name.length}/100</div>
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.rowLabel}>
            <div className={styles.rowLabelMain}>Email</div>
            <div className={styles.rowLabelHint}>
              Identificativo immutabile. Contatta supporto per modificarlo.
            </div>
          </div>
          <div className={styles.rowControl}>
            <input
              type="email"
              className={`${styles.input} ${styles.inputReadonly}`}
              value={profile.email || ''}
              readOnly
              aria-label="Email"
            />
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.rowLabel}>
            <div className={styles.rowLabelMain}>Nota visibile al paziente</div>
            <div className={styles.rowLabelHint}>
              Messaggio fisso mostrato in app paziente. Es. "Ricorda di pesarti il lunedì
              mattina a digiuno".
            </div>
          </div>
          <div className={styles.rowControl}>
            <textarea
              className={styles.textarea}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={2000}
              placeholder="Lascia una nota visibile a tutti i tuoi pazienti…"
              disabled={updateMut.isPending}
              aria-label="Nota paziente"
            />
            <div className={styles.inputCount}>{note.length}/2000</div>
          </div>
        </div>

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={onSave}
            disabled={!dirty || updateMut.isPending || name.trim().length === 0}
          >
            {updateMut.isPending ? (
              <>
                <Loader2 size={14} className={styles.spin} />
                <span>Salvataggio…</span>
              </>
            ) : (
              <>
                <Save size={14} />
                <span>Salva profilo</span>
              </>
            )}
          </button>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 2) Branding studio (logo + colori)
// ---------------------------------------------------------------------------

function BrandingSection({ profile, setToast, refreshAuth }: SectionProps) {
  const [studioName, setStudioName] = useState(profile.studio_name || '');
  const [primary, setPrimary] = useState(profile.studio_color_primary || DEFAULT_PRIMARY);
  const [accent, setAccent] = useState(profile.studio_color_accent || DEFAULT_ACCENT);
  const [logoVersion, setLogoVersion] = useState(0);
  const updateMut = useUpdateStudioProfile();
  const uploadMut = useUploadStudioLogo();
  const deleteMut = useDeleteStudioLogo();

  useEffect(() => {
    setStudioName(profile.studio_name || '');
    setPrimary(profile.studio_color_primary || DEFAULT_PRIMARY);
    setAccent(profile.studio_color_accent || DEFAULT_ACCENT);
  }, [profile.studio_name, profile.studio_color_primary, profile.studio_color_accent]);

  const dirty = useMemo(() => {
    const cleanStudioName = studioName.trim();
    return (
      cleanStudioName !== (profile.studio_name || '') ||
      primary.toLowerCase() !== (profile.studio_color_primary || DEFAULT_PRIMARY).toLowerCase() ||
      accent.toLowerCase() !== (profile.studio_color_accent || DEFAULT_ACCENT).toLowerCase()
    );
  }, [
    studioName,
    primary,
    accent,
    profile.studio_name,
    profile.studio_color_primary,
    profile.studio_color_accent,
  ]);

  const onSaveBranding = () => {
    if (!dirty) return;
    const body: UpdateProfileBody = {};
    const clean = studioName.trim();
    if (clean !== (profile.studio_name || '')) {
      body.studio_name = clean.length > 0 ? clean : null;
    }
    if (primary.toLowerCase() !== (profile.studio_color_primary || '').toLowerCase()) {
      body.studio_color_primary = primary;
    }
    if (accent.toLowerCase() !== (profile.studio_color_accent || '').toLowerCase()) {
      body.studio_color_accent = accent;
    }
    updateMut.mutate(body, {
      onSuccess: () => {
        setToast({ kind: 'success', msg: 'Branding aggiornato' });
        void refreshAuth?.();
      },
      onError: (err) => {
        const msg = err instanceof ApiError ? err.message : err.message;
        setToast({ kind: 'error', msg });
      },
    });
  };

  const onUploadLogo = (file: File) => {
    uploadMut.mutate(file, {
      onSuccess: () => {
        setLogoVersion((v) => v + 1);
        setToast({ kind: 'success', msg: 'Logo caricato' });
      },
      onError: (err) => {
        const msg = err instanceof ApiError ? err.message : err.message;
        setToast({ kind: 'error', msg });
      },
    });
  };

  const onRemoveLogo = () => {
    deleteMut.mutate(undefined, {
      onSuccess: () => {
        setLogoVersion((v) => v + 1);
        setToast({ kind: 'success', msg: 'Logo rimosso' });
      },
      onError: (err) => {
        const msg = err instanceof ApiError ? err.message : err.message;
        setToast({ kind: 'error', msg });
      },
    });
  };

  const logoSrc = profile.studio_logo_url
    ? studioLogoSrc(profile, profile.studio_logo_r2_key
        ? `${profile.studio_logo_r2_key}-${logoVersion}`
        : String(logoVersion))
    : null;

  const initials = (studioName || profile.name)
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <h2 className={styles.cardTitle}>Branding studio</h2>
        <p className={styles.cardSub}>
          Logo + colori che appaiono in app paziente, PDF e comunicazioni.
        </p>
      </header>
      <div className={styles.cardBody}>
        <div className={styles.row}>
          <div className={styles.rowLabel}>
            <div className={styles.rowLabelMain}>Nome studio</div>
            <div className={styles.rowLabelHint}>
              Es. "Studio Nutrizionale Rossi". Vuoto = mostra il tuo nome.
            </div>
          </div>
          <div className={styles.rowControl}>
            <input
              type="text"
              className={styles.input}
              value={studioName}
              onChange={(e) => setStudioName(e.target.value)}
              maxLength={100}
              placeholder="Studio Nutrizionale…"
              disabled={updateMut.isPending}
              aria-label="Nome studio"
            />
            <div className={styles.inputCount}>{studioName.length}/100</div>
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.rowLabel}>
            <div className={styles.rowLabelMain}>Logo</div>
            <div className={styles.rowLabelHint}>
              PNG, JPG o SVG · max 2 MB. Sostituisce automaticamente il precedente.
            </div>
          </div>
          <div className={styles.rowControl}>
            <LogoUploader
              currentLogoUrl={logoSrc}
              onUpload={onUploadLogo}
              onRemove={onRemoveLogo}
              isUploading={uploadMut.isPending}
              isRemoving={deleteMut.isPending}
            />
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.rowLabel}>
            <div className={styles.rowLabelMain}>Colori</div>
            <div className={styles.rowLabelHint}>
              Primario per CTA e accenti; secondario per badge.
            </div>
          </div>
          <div className={styles.rowControl}>
            <div className={styles.colorRow}>
              <ColorPicker
                label="Primario"
                value={primary}
                defaultColor={DEFAULT_PRIMARY}
                onChange={setPrimary}
                disabled={updateMut.isPending}
                hint="Default: verde DietAmenu"
              />
              <ColorPicker
                label="Accento"
                value={accent}
                defaultColor={DEFAULT_ACCENT}
                onChange={setAccent}
                disabled={updateMut.isPending}
                hint="Default: arancio coach"
              />
            </div>

            <div className={styles.brandPreview}>
              <div className={styles.brandPreviewLbl}>Anteprima</div>
              <div className={styles.brandPreviewBody}>
                <div
                  className={styles.brandPreviewLogo}
                  style={{ background: primary }}
                  aria-hidden
                >
                  {logoSrc ? <img src={logoSrc} alt="" /> : initials || 'ST'}
                </div>
                <div>
                  <div className={styles.brandPreviewName}>
                    {studioName.trim() || profile.name || 'Studio'}
                  </div>
                  <div className={styles.brandPreviewSwatches}>
                    <span className={styles.brandPreviewSwatch} style={{ background: primary }} />
                    <span className={styles.brandPreviewSwatch} style={{ background: accent }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={onSaveBranding}
            disabled={!dirty || updateMut.isPending}
          >
            {updateMut.isPending ? (
              <>
                <Loader2 size={14} className={styles.spin} />
                <span>Salvataggio…</span>
              </>
            ) : (
              <>
                <Save size={14} />
                <span>Salva branding</span>
              </>
            )}
          </button>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 3) Figura professionale (pro_kind)
// ---------------------------------------------------------------------------

const KIND_OPTIONS: { kind: StudioProKind; title: string; desc: string }[] = [
  {
    kind: 'nutri',
    title: 'Nutrizionista',
    desc: 'Gestisci piani alimentari e visite di follow-up nutrizionale.',
  },
  {
    kind: 'coach',
    title: 'Coach personal trainer',
    desc: 'Crea programmi di allenamento, sessioni live e monitora PR atleti.',
  },
  {
    kind: 'both',
    title: 'Entrambi (nutri + coach)',
    desc: 'Attiva il role-switcher nella sidebar per passare tra moduli.',
  },
];

function RoleSection({ profile, setToast, refreshAuth }: SectionProps) {
  const [kind, setKind] = useState<StudioProKind | null>(profile.pro_kind);
  const updateMut = useUpdateStudioProfile();

  useEffect(() => {
    setKind(profile.pro_kind);
  }, [profile.pro_kind]);

  const dirty = kind !== profile.pro_kind && kind !== null;
  // Warning: passa da 'both' a 'nutri' o 'coach' — la UI mostra avviso
  const showWarning =
    profile.pro_kind === 'both' && (kind === 'nutri' || kind === 'coach');

  const onSaveRole = () => {
    if (!dirty || !kind) return;
    updateMut.mutate({ pro_kind: kind }, {
      onSuccess: () => {
        setToast({ kind: 'success', msg: 'Ruolo aggiornato' });
        void refreshAuth?.();
      },
      onError: (err) => {
        const msg = err instanceof ApiError ? err.message : err.message;
        setToast({ kind: 'error', msg });
      },
    });
  };

  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <h2 className={styles.cardTitle}>Figura professionale</h2>
        <p className={styles.cardSub}>
          Determina il modulo attivo in app e quali pazienti puoi gestire.
        </p>
      </header>
      <div className={styles.cardBody}>
        <div className={styles.radioGroup} role="radiogroup" aria-label="Figura professionale">
          {KIND_OPTIONS.map((opt) => {
            const active = kind === opt.kind;
            return (
              <label
                key={opt.kind}
                className={`${styles.radioCard} ${active ? styles.radioCardActive : ''}`}
              >
                <input
                  type="radio"
                  name="pro_kind"
                  value={opt.kind}
                  checked={active}
                  onChange={() => setKind(opt.kind)}
                  className={styles.radioInput}
                  disabled={updateMut.isPending}
                />
                <div className={styles.radioBody}>
                  <div className={styles.radioTitle}>{opt.title}</div>
                  <div className={styles.radioDesc}>{opt.desc}</div>
                </div>
              </label>
            );
          })}
        </div>

        {showWarning && (
          <div className={styles.warning} role="alert">
            <AlertTriangle size={16} className={styles.warningIcon} />
            <div>
              Cambiando ruolo, eventuali pazienti/atleti gestiti con il ruolo opposto
              resteranno legati al tuo account ma il modulo corrispondente
              (nutri o coach) non sarà più visibile nella tua sidebar.
              Puoi tornare a "Entrambi" in qualsiasi momento.
            </div>
          </div>
        )}

        <div className={`${styles.actions} ${styles.actionsCompact}`}>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={onSaveRole}
            disabled={!dirty || updateMut.isPending}
          >
            {updateMut.isPending ? (
              <>
                <Loader2 size={14} className={styles.spin} />
                <span>Salvataggio…</span>
              </>
            ) : (
              <>
                <Save size={14} />
                <span>Salva ruolo</span>
              </>
            )}
          </button>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 4) Codice invito paziente
// ---------------------------------------------------------------------------

type CopiedTarget = 'code' | 'link' | 'code_nutri' | 'link_nutri' | 'code_coach' | 'link_coach';

function InviteCodeSection({ profile, setToast }: SectionProps) {
  const [copied, setCopied] = useState<CopiedTarget | null>(null);
  const code = profile.pro_code || '——————';
  const inviteLink = profile.pro_code
    ? `${INVITE_LINK_BASE}?invite=${encodeURIComponent(profile.pro_code)}`
    : '';

  // Fase 3 — codici per-ruolo, presenti solo per pro_kind='both'.
  const showPerRoleCodes = profile.pro_kind === 'both' && !!(profile.pro_code_nutri || profile.pro_code_coach);
  const inviteLinkNutri = profile.pro_code_nutri
    ? `${INVITE_LINK_BASE}?invite=${encodeURIComponent(profile.pro_code_nutri)}`
    : '';
  const inviteLinkCoach = profile.pro_code_coach
    ? `${INVITE_LINK_BASE}?invite=${encodeURIComponent(profile.pro_code_coach)}`
    : '';

  const copyValue = (value: string, what: CopiedTarget) => {
    if (!value) return;
    void (async () => {
      try {
        await navigator.clipboard.writeText(value);
        setCopied(what);
        const isCode = what.startsWith('code');
        setToast({ kind: 'success', msg: isCode ? 'Codice copiato' : 'Link copiato' });
        setTimeout(() => setCopied(null), 1500);
      } catch {
        setToast({ kind: 'error', msg: 'Copia non riuscita: copia manualmente' });
      }
    })();
  };

  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <h2 className={styles.cardTitle}>Codice invito paziente</h2>
        <p className={styles.cardSub}>
          Condividi codice o link onboarding: il paziente verrà associato automaticamente al tuo studio.
        </p>
      </header>
      <div className={styles.cardBody}>
        <div className={styles.row}>
          <div className={styles.rowLabel}>
            <div className={styles.rowLabelMain}>Codice</div>
            <div className={styles.rowLabelHint}>
              6 caratteri univoci. Il paziente lo inserisce in onboarding.
            </div>
          </div>
          <div className={styles.rowControl}>
            <div className={styles.codeBlock}>
              <div className={styles.codeValue}>{code}</div>
              <div className={styles.codeRow}>
                <button
                  type="button"
                  className={styles.btnGhost}
                  onClick={() => copyValue(profile.pro_code || '', 'code')}
                  disabled={!profile.pro_code}
                >
                  {copied === 'code' ? <Check size={14} /> : <Copy size={14} />}
                  <span>{copied === 'code' ? 'Copiato' : 'Copia codice'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.rowLabel}>
            <div className={styles.rowLabelMain}>Link invito</div>
            <div className={styles.rowLabelHint}>
              Il paziente clicca e completa l'onboarding senza inserire codice.
            </div>
          </div>
          <div className={styles.rowControl}>
            <div className={styles.codeBlock}>
              <div className={styles.codeRow}>
                <div className={styles.codeLink}>{inviteLink || '—'}</div>
                <button
                  type="button"
                  className={styles.btnGhost}
                  onClick={() => copyValue(inviteLink, 'link')}
                  disabled={!inviteLink}
                >
                  {copied === 'link' ? <Check size={14} /> : <Copy size={14} />}
                  <span>{copied === 'link' ? 'Copiato' : 'Copia link'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {showPerRoleCodes && (
          <>
            <div className={styles.row}>
              <div className={styles.rowLabel}>
                <div className={styles.rowLabelMain}>Codici separati per percorso</div>
                <div className={styles.rowLabelHint}>
                  Con i codici separati il cliente si collega solo al percorso che gli serve.
                  Il codice principale li collega a entrambi.
                </div>
              </div>
              <div className={styles.rowControl} />
            </div>

            <div className={styles.row}>
              <div className={styles.rowLabel}>
                <div className={styles.rowLabelMain}>Codice Nutrizione</div>
                <div className={styles.rowLabelHint}>
                  Il paziente si aggancia solo come cliente nutrizione.
                </div>
              </div>
              <div className={styles.rowControl}>
                <div className={styles.codeBlock}>
                  <div className={styles.codeValue}>{profile.pro_code_nutri || '——————'}</div>
                  <div className={styles.codeRow}>
                    <button
                      type="button"
                      className={styles.btnGhost}
                      onClick={() => copyValue(profile.pro_code_nutri || '', 'code_nutri')}
                      disabled={!profile.pro_code_nutri}
                    >
                      {copied === 'code_nutri' ? <Check size={14} /> : <Copy size={14} />}
                      <span>{copied === 'code_nutri' ? 'Copiato' : 'Copia codice'}</span>
                    </button>
                  </div>
                  <div className={styles.codeRow}>
                    <div className={styles.codeLink}>{inviteLinkNutri || '—'}</div>
                    <button
                      type="button"
                      className={styles.btnGhost}
                      onClick={() => copyValue(inviteLinkNutri, 'link_nutri')}
                      disabled={!inviteLinkNutri}
                    >
                      {copied === 'link_nutri' ? <Check size={14} /> : <Copy size={14} />}
                      <span>{copied === 'link_nutri' ? 'Copiato' : 'Copia link'}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.row}>
              <div className={styles.rowLabel}>
                <div className={styles.rowLabelMain}>Codice Coaching</div>
                <div className={styles.rowLabelHint}>
                  Il paziente si aggancia solo come atleta.
                </div>
              </div>
              <div className={styles.rowControl}>
                <div className={styles.codeBlock}>
                  <div className={styles.codeValue}>{profile.pro_code_coach || '——————'}</div>
                  <div className={styles.codeRow}>
                    <button
                      type="button"
                      className={styles.btnGhost}
                      onClick={() => copyValue(profile.pro_code_coach || '', 'code_coach')}
                      disabled={!profile.pro_code_coach}
                    >
                      {copied === 'code_coach' ? <Check size={14} /> : <Copy size={14} />}
                      <span>{copied === 'code_coach' ? 'Copiato' : 'Copia codice'}</span>
                    </button>
                  </div>
                  <div className={styles.codeRow}>
                    <div className={styles.codeLink}>{inviteLinkCoach || '—'}</div>
                    <button
                      type="button"
                      className={styles.btnGhost}
                      onClick={() => copyValue(inviteLinkCoach, 'link_coach')}
                      disabled={!inviteLinkCoach}
                    >
                      {copied === 'link_coach' ? <Check size={14} /> : <Copy size={14} />}
                      <span>{copied === 'link_coach' ? 'Copiato' : 'Copia link'}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 4.5) Integrazione calendario esterno (Cal.com OAuth) — Sprint 8.4
// ---------------------------------------------------------------------------

interface CalcomSectionProps {
  setToast: (t: ToastState | null) => void;
}

function formatRelativeIt(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = Date.parse(iso);
  if (isNaN(d)) return '—';
  const diff = Date.now() - d;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return 'pochi secondi fa';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} minut${min === 1 ? 'o' : 'i'} fa`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} or${hr === 1 ? 'a' : 'e'} fa`;
  const days = Math.floor(hr / 24);
  if (days < 30) return `${days} giorn${days === 1 ? 'o' : 'i'} fa`;
  return new Date(d).toLocaleDateString('it-IT');
}

function CalcomSection({ setToast }: CalcomSectionProps) {
  const statusQ = useCalcomStatus();
  const disconnectMut = useDisconnectCalcom();
  const syncMut = useSyncCalcom();
  const [lastSyncResult, setLastSyncResult] = useState<CalcomSyncResult | null>(null);

  const status = statusQ.data;
  const configured = status?.configured ?? false;
  const connected = status?.connected ?? false;
  const hasError = !!status?.last_error;

  const onConnect = () => {
    window.location.href = calcomConnectUrl();
  };

  const onDisconnect = () => {
    if (!window.confirm('Disconnettere il tuo account Cal.com? Gli appuntamenti già importati restano in archivio.')) {
      return;
    }
    disconnectMut.mutate(undefined, {
      onSuccess: () => {
        setToast({ kind: 'success', msg: 'Cal.com disconnesso' });
        setLastSyncResult(null);
      },
      onError: (err) => {
        const msg = err instanceof ApiError ? err.message : err.message;
        setToast({ kind: 'error', msg });
      },
    });
  };

  const onSync = () => {
    syncMut.mutate(undefined, {
      onSuccess: (data) => {
        setLastSyncResult(data);
        const summary = `Importati ${data.imported}, aggiornati ${data.updated}, saltati ${data.skipped}`;
        setToast({ kind: 'success', msg: `Sync completato — ${summary}` });
      },
      onError: (err) => {
        const msg = err instanceof ApiError ? err.message : err.message;
        setToast({ kind: 'error', msg });
      },
    });
  };

  let stateBoxClass = styles.calcomStateRow;
  if (hasError) stateBoxClass += ' ' + styles.calcomStateRowError;
  else if (connected) stateBoxClass += ' ' + styles.calcomStateRowConnected;
  else if (!configured) stateBoxClass += ' ' + styles.calcomStateRowPending;

  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <h2 className={styles.cardTitle}>Integrazione calendario esterno</h2>
        <p className={styles.cardSub}>
          Connetti il tuo account Cal.com per sincronizzare event types e bookings.
          Webhook automatico + sync ogni 30 minuti.
        </p>
      </header>
      <div className={styles.cardBody}>
        {/* Stato */}
        <div className={stateBoxClass}>
          {!configured ? (
            <>
              <div className={styles.calcomStateTitle}>
                <Clock size={16} />
                <span className={`${styles.calcomStateLabel} ${styles.calcomStateLabelPending}`}>In setup</span>
              </div>
              <div className={styles.calcomMeta}>
                Integrazione Cal.com in setup. L'amministratore deve completare la registrazione OAuth client su Cal.com.
              </div>
            </>
          ) : !connected ? (
            <>
              <div className={styles.calcomStateTitle}>
                <Link2Off size={16} />
                <span className={`${styles.calcomStateLabel} ${styles.calcomStateLabelPending}`}>Non collegato</span>
              </div>
              <div className={styles.calcomMeta}>
                Collega il tuo account Cal.com per importare i tuoi bookings come appuntamenti.
              </div>
              <div className={styles.calcomActions}>
                <button type="button" className={styles.btnPrimary} onClick={onConnect}>
                  <Link2 size={14} />
                  <span>Connetti Cal.com</span>
                </button>
              </div>
            </>
          ) : (
            <>
              <div className={styles.calcomStateTitle}>
                <Calendar size={16} />
                {hasError ? (
                  <span className={`${styles.calcomStateLabel} ${styles.calcomStateLabelError}`}>Errore</span>
                ) : (
                  <span className={styles.calcomStateLabel}>Connesso</span>
                )}
              </div>
              <div className={styles.calcomMeta}>
                {status?.cal_username && (
                  <div className={styles.calcomMetaItem}>
                    Account: <strong>@{status.cal_username}</strong>
                  </div>
                )}
                {status?.cal_email && (
                  <div className={styles.calcomMetaItem}>
                    Email: <strong>{status.cal_email}</strong>
                  </div>
                )}
                <div className={styles.calcomMetaItem}>
                  Ultimo sync: <strong>{formatRelativeIt(status?.last_synced_at)}</strong>
                </div>
                {hasError && (
                  <div className={styles.calcomMetaItem} role="alert">
                    Ultimo errore: <strong>{status?.last_error}</strong>{' '}
                    ({formatRelativeIt(status?.last_error_at)})
                  </div>
                )}
              </div>
              <div className={styles.calcomActions}>
                <button
                  type="button"
                  className={styles.btnPrimary}
                  onClick={onSync}
                  disabled={syncMut.isPending}
                >
                  {syncMut.isPending ? (
                    <>
                      <Loader2 size={14} className={styles.spin} />
                      <span>Sincronizzazione…</span>
                    </>
                  ) : (
                    <>
                      <RefreshCw size={14} />
                      <span>Sincronizza ora</span>
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className={styles.btnDanger}
                  onClick={onDisconnect}
                  disabled={disconnectMut.isPending}
                >
                  {disconnectMut.isPending ? <Loader2 size={14} className={styles.spin} /> : <Link2Off size={14} />}
                  <span>Disconnetti</span>
                </button>
                {hasError && (
                  <button
                    type="button"
                    className={styles.btnGhost}
                    onClick={onConnect}
                  >
                    <Link2 size={14} />
                    <span>Riprova connessione</span>
                  </button>
                )}
              </div>

              {lastSyncResult && (
                <>
                  <div className={styles.calcomSyncStats}>
                    <span><strong>Imported:</strong> {lastSyncResult.imported}</span>
                    <span><strong>Updated:</strong> {lastSyncResult.updated}</span>
                    <span><strong>Skipped:</strong> {lastSyncResult.skipped}</span>
                    {lastSyncResult.errors > 0 && (
                      <span><strong>Errori:</strong> {lastSyncResult.errors}</span>
                    )}
                  </div>
                  {lastSyncResult.conflicts.length > 0 && (
                    <div className={styles.calcomConflictList}>
                      <strong>{lastSyncResult.conflicts.length} conflitt{lastSyncResult.conflicts.length === 1 ? 'o' : 'i'} con appuntamenti manuali:</strong>
                      <ul>
                        {lastSyncResult.conflicts.map((c) => (
                          <li key={c.cal_booking_id}>
                            {c.patient_name || 'Paziente'} — Cal.com {new Date(c.cal_start_at).toLocaleString('it-IT')} sovrapposto a manuale {new Date(c.manual_start_at).toLocaleString('it-IT')}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 5) Account & sicurezza
// ---------------------------------------------------------------------------

type PricingPlan = {
  id: string;
  slug: string;
  role: string;
  display_name: string;
  price_cents: number;
  billing_interval: string | null;
  trial_days: number;
};

function AccountSection({ profile, setToast }: SectionProps) {
  const { logout } = useAuth();
  const [portalLoading, setPortalLoading] = useState(false);
  // Guard portale fatturazione: /api/billing/me espone customer_portal_available
  // (= esiste uno stripe_customer_id). Se false (es. account seed o pre-checkout)
  // mostriamo un messaggio invece di tentare il portale → niente "Failed to fetch".
  const [portalAvailable, setPortalAvailable] = useState<boolean | null>(null);
  // Go-live 2026-06-10: senza customer Stripe il portale non esiste → il pro deve
  // poter ATTIVARE l'abbonamento da qui (prima il checkout non era esposto da
  // nessuna UI: backend pronto, bottone mancante).
  const [proPlans, setProPlans] = useState<PricingPlan[]>([]);
  const [checkoutPlanId, setCheckoutPlanId] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/pricing`);
        if (!res.ok) return;
        const data = await res.json() as { plans?: PricingPlan[] };
        setProPlans((data.plans || []).filter((p) => p.role === 'dietologist' && p.price_cents > 0));
      } catch {
        // pricing non disponibile → il blocco piani semplicemente non appare
      }
    })();
  }, []);

  const startCheckout = (planId: string) => {
    setCheckoutPlanId(planId);
    void (async () => {
      try {
        const sid = getStoredSessionId();
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (sid) headers['X-Session-Id'] = sid; // CHIPS-safe
        const res = await fetch(`${API_BASE}/api/billing/checkout`, {
          method: 'POST',
          credentials: 'include',
          headers,
          body: JSON.stringify({ plan_id: planId }),
        });
        const data = await res.json().catch(() => null) as { url?: string; error?: string } | null;
        if (!res.ok || !data?.url) throw new Error(data?.error || `HTTP ${res.status}`);
        window.location.href = data.url; // checkout Stripe hosted
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Errore avvio checkout';
        setToast({ kind: 'error', msg });
        setCheckoutPlanId(null);
      }
    })();
  };

  useEffect(() => {
    void (async () => {
      try {
        const sid = getStoredSessionId();
        const headers: Record<string, string> = {};
        if (sid) headers['X-Session-Id'] = sid;
        const res = await fetch(`${API_BASE}/api/billing/me`, { credentials: 'include', headers });
        if (res.ok) {
          const data = await res.json() as { customer_portal_available?: boolean };
          setPortalAvailable(!!data.customer_portal_available);
        } else {
          setPortalAvailable(false);
        }
      } catch {
        setPortalAvailable(false);
      }
    })();
  }, []);

  const subBadgeCls =
    profile.subscription_status === 'active' || profile.subscription_status === 'trialing'
      ? styles.subBadge
      : profile.subscription_status === 'past_due' || profile.subscription_status === 'canceled'
        ? `${styles.subBadge} ${styles.subBadgePast}`
        : `${styles.subBadge} ${styles.subBadgeNeutral}`;

  const subLabel = profile.subscription_status
    ? profile.subscription_status.replace('_', ' ')
    : 'nessun abbonamento';
  const planLabel = profile.subscription_plan_id || '—';

  // Countdown trial + soft drop (paywall.ts GRACE_PERIOD_DAYS=7): alla scadenza
  // della prova partono 7 giorni di accesso di cortesia, poi chiusura totale (402).
  const TRIAL_GRACE_DAYS = 7;
  const fmtDate = (d: Date) => d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const trialCountdown = (() => {
    if (profile.subscription_status !== 'trialing' || !profile.subscription_current_period_end) return null;
    const end = new Date(profile.subscription_current_period_end);
    if (isNaN(end.getTime())) return null;
    const graceEnd = new Date(end.getTime() + TRIAL_GRACE_DAYS * 86400000);
    const daysLeft = Math.ceil((end.getTime() - Date.now()) / 86400000);
    if (daysLeft > 0) return { kind: 'trial' as const, days: daysLeft, end, graceEnd };
    const graceDays = Math.ceil((graceEnd.getTime() - Date.now()) / 86400000);
    if (graceDays > 0) return { kind: 'grace' as const, days: graceDays, end, graceEnd };
    return { kind: 'over' as const, days: 0, end, graceEnd };
  })();

  const openPortal = () => {
    setPortalLoading(true);
    void (async () => {
      try {
        const sid = getStoredSessionId();
        const headers: Record<string, string> = {};
        if (sid) headers['X-Session-Id'] = sid; // CHIPS-safe: cookie può essere partitioned
        const res = await fetch(`${API_BASE}/api/billing/portal`, {
          method: 'GET',
          credentials: 'include',
          headers,
        });
        if (!res.ok) {
          const data = await res.json().catch(() => null) as { error?: string } | null;
          if (res.status === 400 && data?.error === 'no stripe customer') {
            throw new Error('Nessun cliente di fatturazione associato: il portale è disponibile dopo il primo pagamento.');
          }
          throw new Error(data?.error || `HTTP ${res.status}`);
        }
        const data = await res.json() as { url?: string };
        if (data.url) {
          window.location.href = data.url;
        } else {
          throw new Error('URL portale mancante');
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Errore portale';
        setToast({ kind: 'error', msg });
      } finally {
        setPortalLoading(false);
      }
    })();
  };

  const onLogout = () => {
    void logout();
  };

  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <h2 className={styles.cardTitle}>Account & sicurezza</h2>
        <p className={styles.cardSub}>
          Abbonamento, fatturazione e gestione sessione.
        </p>
      </header>
      <div className={styles.cardBody}>
        <div className={styles.row}>
          <div className={styles.rowLabel}>
            <div className={styles.rowLabelMain}>Abbonamento</div>
            <div className={styles.rowLabelHint}>
              Status corrente del piano DietAmenu Pro.
            </div>
          </div>
          <div className={styles.rowControl}>
            <div className={styles.subRow}>
              <span className={subBadgeCls}>{subLabel}</span>
              <span>
                Piano: <strong>{planLabel}</strong>
              </span>
            </div>
            {trialCountdown && (
              <div style={{ marginTop: 8, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                {trialCountdown.kind === 'trial' && (
                  <span>
                    ⏳ Prova: <strong>{trialCountdown.days} {trialCountdown.days === 1 ? 'giorno' : 'giorni'}</strong> rimanenti
                    (fino al {fmtDate(trialCountdown.end)}) — poi {TRIAL_GRACE_DAYS} giorni di cortesia,
                    chiusura il <strong>{fmtDate(trialCountdown.graceEnd)}</strong>.
                  </span>
                )}
                {trialCountdown.kind === 'grace' && (
                  <span style={{ color: '#b45309', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <AlertTriangle size={14} />
                    Prova scaduta — accesso di cortesia: <strong>{trialCountdown.days} {trialCountdown.days === 1 ? 'giorno' : 'giorni'}</strong> alla
                    chiusura ({fmtDate(trialCountdown.graceEnd)}). Scegli un piano per non interrompere lo studio.
                  </span>
                )}
                {trialCountdown.kind === 'over' && (
                  <span style={{ color: '#b91c1c', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <AlertTriangle size={14} />
                    Accesso scaduto il {fmtDate(trialCountdown.graceEnd)} — scegli un piano per riattivare lo studio.
                  </span>
                )}
              </div>
            )}
            <div className={styles.codeRow} style={{ marginTop: 12 }}>
              {portalAvailable === false ? (
                <span className={styles.rowLabelHint}>
                  Nessun abbonamento Stripe da gestire. Il portale di fatturazione sarà
                  disponibile dopo il primo pagamento.
                </span>
              ) : (
                <button
                  type="button"
                  className={styles.btnGhost}
                  onClick={openPortal}
                  disabled={portalLoading || portalAvailable === null}
                >
                  {portalLoading ? <Loader2 size={14} className={styles.spin} /> : <ExternalLink size={14} />}
                  <span>{portalLoading ? 'Apertura…' : 'Apri portale fatturazione'}</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {portalAvailable === false && proPlans.length > 0 && (
          <div className={styles.row}>
            <div className={styles.rowLabel}>
              <div className={styles.rowLabelMain}>Scegli il piano</div>
              <div className={styles.rowLabelHint}>
                Attiva l&apos;abbonamento con checkout sicuro Stripe.
                {proPlans[0]?.trial_days ? ` ${proPlans[0].trial_days} giorni di prova inclusi.` : ''}
              </div>
            </div>
            <div className={styles.rowControl}>
              <div className={styles.codeRow} style={{ flexWrap: 'wrap', gap: 8 }}>
                {proPlans.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className={styles.btnGhost}
                    onClick={() => startCheckout(p.id)}
                    disabled={checkoutPlanId !== null}
                  >
                    {checkoutPlanId === p.id ? <Loader2 size={14} className={styles.spin} /> : <CreditCard size={14} />}
                    <span>
                      {p.display_name} — {(p.price_cents / 100).toLocaleString('it-IT', { minimumFractionDigits: p.price_cents % 100 === 0 ? 0 : 2 })}€/mese
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className={styles.row}>
          <div className={styles.rowLabel}>
            <div className={styles.rowLabelMain}>Sessione</div>
            <div className={styles.rowLabelHint}>
              Esci da questo browser. Le sessioni su altri device restano attive
              fino a scadenza naturale.
            </div>
          </div>
          <div className={styles.rowControl}>
            <div className={styles.codeRow}>
              <button
                type="button"
                className={styles.btnGhost}
                onClick={onLogout}
              >
                <LogOut size={14} />
                <span>Disconnetti</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 6) Consumo del piano (badge pazienti/AI) — Sprint 10 Fase 3/F5
// ---------------------------------------------------------------------------

/** Percentuale used/limit, clampata 0-100. limit null → 0 (illimitato, niente barra). */
function usagePct(used: number, limit: number | null): number {
  if (limit === null || limit <= 0) return 0;
  return Math.min(100, Math.max(0, (used / limit) * 100));
}

function usageSeverity(used: number, limit: number | null): 'ok' | 'warn' | 'danger' {
  if (limit === null) return 'ok';
  if (used >= limit) return 'danger';
  if (used / limit >= 0.8) return 'warn';
  return 'ok';
}

function UsageBar({ used, limit }: { used: number; limit: number | null }) {
  if (limit === null) return null;
  const pct = usagePct(used, limit);
  const severity = usageSeverity(used, limit);
  const fillCls =
    severity === 'danger'
      ? `${styles.usageBarFill} ${styles.usageBarFillDanger}`
      : severity === 'warn'
        ? `${styles.usageBarFill} ${styles.usageBarFillWarn}`
        : styles.usageBarFill;
  return (
    <div
      className={styles.usageBar}
      role="progressbar"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div className={fillCls} style={{ width: `${pct}%` }} />
    </div>
  );
}

function UsageSection() {
  const subQ = useMySubscription();
  const [addonBusy, setAddonBusy] = useState<'ai_pack' | 'module' | null>(null);
  const [addonMsg, setAddonMsg] = useState<string | null>(null);
  const usage = subQ.data?.usage;

  // Solo per i pro (usage null per il paziente, ma questa pagina è pro-only).
  // Se il fetch fallisce o non c'è ancora dato, la sezione semplicemente non appare
  // (nessun errore bloccante: è un badge informativo, non critico).
  if (subQ.isLoading || !usage) return null;

  const patientsSeverity = usageSeverity(usage.patients.used, usage.patients.cap);
  const aiSeverity = usageSeverity(usage.ai.used, usage.ai.quota);

  const patientsValueCls =
    patientsSeverity === 'danger'
      ? `${styles.usageItemValue} ${styles.usageItemValueDanger}`
      : patientsSeverity === 'warn'
        ? `${styles.usageItemValue} ${styles.usageItemValueWarn}`
        : styles.usageItemValue;

  const aiValueCls =
    aiSeverity === 'danger'
      ? `${styles.usageItemValue} ${styles.usageItemValueDanger}`
      : aiSeverity === 'warn'
        ? `${styles.usageItemValue} ${styles.usageItemValueWarn}`
        : styles.usageItemValue;

  const credits = usage.ai.credits ?? 0;
  const moduleActive = subQ.data?.module_addon_active ?? false;
  const buyAddon = (kind: 'ai_pack' | 'module') => {
    setAddonBusy(kind);
    setAddonMsg(null);
    (kind === 'ai_pack' ? buyAiPack() : buyModuleAddon())
      .then((r) => { window.location.href = r.url; })
      .catch((err) => {
        const code = err instanceof ApiError ? err.code : '';
        setAddonMsg(code === 'addon_not_available' ? 'Add-on non ancora disponibile — a breve!' : 'Errore avvio acquisto. Riprova.');
        setAddonBusy(null);
      });
  };

  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <h2 className={styles.cardTitle}>Consumo del piano</h2>
        <p className={styles.cardSub}>
          Pazienti gestiti e azioni AI usate nel mese corrente, rispetto ai limiti del tuo piano.
        </p>
      </header>
      <div className={styles.cardBody}>
        <div className={styles.usageBlock}>
          <div className={styles.usageItem}>
            <div className={styles.usageItemHead}>
              <span className={styles.usageItemLabel}>Pazienti</span>
              <span className={patientsValueCls}>
                {usage.patients.used} / {usage.patients.cap ?? '∞'}
                {usage.patients.cap === null ? ' (illimitati)' : ''}
              </span>
            </div>
            <UsageBar used={usage.patients.used} limit={usage.patients.cap} />
          </div>

          <div className={styles.usageItem}>
            <div className={styles.usageItemHead}>
              <span className={styles.usageItemLabel}>Azioni AI questo mese</span>
              <span className={aiValueCls}>
                {usage.ai.used} / {usage.ai.quota ?? '∞'}
                {usage.ai.quota === null ? ' (illimitate)' : ''}
              </span>
            </div>
            <UsageBar used={usage.ai.used} limit={usage.ai.quota} />
            {credits > 0 && (
              <div style={{ fontSize: 12, color: 'var(--green-deep, #0b8a3e)', fontWeight: 700, marginTop: 4 }}>
                +{credits} crediti AI extra disponibili
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 16 }}>
          <button type="button" className={styles.btnGhost} disabled={addonBusy !== null} onClick={() => buyAddon('ai_pack')}>
            {addonBusy === 'ai_pack' ? <Loader2 size={14} className={styles.spin} /> : <CreditCard size={14} />}
            <span>Compra 100 azioni AI — 9€</span>
          </button>
          {moduleActive ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, fontWeight: 700, color: 'var(--green-deep, #0b8a3e)' }}>
              <Check size={14} /> 2° modulo attivo
            </span>
          ) : (
            <button type="button" className={styles.btnGhost} disabled={addonBusy !== null} onClick={() => buyAddon('module')}>
              {addonBusy === 'module' ? <Loader2 size={14} className={styles.spin} /> : <CreditCard size={14} />}
              <span>Aggiungi 2° modulo — 9€/mese</span>
            </button>
          )}
        </div>
        {addonMsg && <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--mute, #555)' }}>{addonMsg}</div>}
      </div>
    </section>
  );
}
